import { type BoardState, type Item } from '@shared/board'
import { cardField } from '@shared/card-schema'
import { readableLine } from '@shared/search'
import { type SessionMeta } from '@shared/session'
import * as Y from 'yjs'

/** Text of a card's first block (its title), read straight from the Y doc. */
export function cardTitle(doc: Y.Doc, id: string): string {
  const first = doc.getXmlFragment(cardField(id)).get(0)
  if (!(first instanceof Y.XmlElement)) return ''
  const parts: string[] = []
  const walk = (n: Y.XmlElement | Y.XmlText) => {
    if (n instanceof Y.XmlText) {
      for (const d of n.toDelta() as { insert?: unknown }[]) {
        if (typeof d.insert === 'string') parts.push(d.insert)
      }
    } else
      for (const c of n.toArray()) if (c instanceof Y.XmlElement || c instanceof Y.XmlText) walk(c)
  }
  walk(first)
  return parts.join('').trim()
}

/** "p.12 · first words of its text": a clip's short name. */
export function clipLabel(d: { page: number; text?: string | undefined }): string {
  const words = (d.text ?? '').replace(/\s+/g, ' ').trim().slice(0, 32)
  return words ? `p.${d.page} · ${words}` : `p.${d.page}`
}

/** A conversation's title by id, for the conversations placed on the board. */
export type SessionTitle = (sessionId: string) => string

/** A short human label for an item, for mention chips and lists. */
export function itemLabel(item: Item, doc: Y.Doc, sessionTitle?: SessionTitle): string {
  switch (item.kind) {
    case 'card':
      return cardTitle(doc, item.id)
    case 'message':
      // As it reads: a message is markdown (emphasis, links) like the AI's.
      return item.data.text
        .split('\n')
        .map(readableLine)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 40)
    case 'link':
      return item.data.title ?? item.data.url
    case 'group':
      return item.data.title
    case 'image':
    case 'file':
      return item.data.name
    case 'session':
      return sessionTitle?.(item.data.sessionId) ?? ''
    case 'material':
      return item.data.name
    case 'clip':
      return clipLabel(item.data)
  }
}

export function labelOf(
  s: BoardState,
  doc: Y.Doc,
  id: string,
  sessionTitle?: SessionTitle,
): string | null {
  const it = s.items.get(id)
  return it ? itemLabel(it, doc, sessionTitle) : null
}

export function sessionName(s: SessionMeta | undefined, untitled: string): string {
  return s?.title || untitled
}

export function formatUsd(micros: number): string {
  const usd = micros / 1e6
  if (usd > 0 && usd < 0.01) return '< US$0.01'
  return `US$${usd.toFixed(2)}`
}
